/**
 * Volume Profile Store
 *
 * Zustand store for managing volume profile settings and data.
 * Handles settings sync with backend, footprint data, and delta history.
 */

import { create } from 'zustand';
import { CONFIG } from '../config';
import {
  VolumeProfileSettings,
  FootprintData,
  DeltaData,
  VolumeProfileSnapshot,
  DEFAULT_VOLUME_PROFILE_SETTINGS,
  MAX_FOOTPRINTS_PER_SYMBOL,
  MAX_DELTA_POINTS_PER_SYMBOL,
} from '../types/volumeProfile';

// ============================================================================
// Store Interface
// ============================================================================

interface VolumeProfileState {
  // Settings
  settings: VolumeProfileSettings;

  // Data per symbol
  footprintData: Record<string, FootprintData[]>;
  deltaHistory: Record<string, DeltaData[]>;
  profileSnapshots: Record<string, VolumeProfileSnapshot>;

  // Sync state
  isSyncing: boolean;
  lastSyncAt: number | null;
  lastSyncError: string | null;

  // Settings actions
  setSettings: (settings: VolumeProfileSettings) => void;
  updateSettings: (partial: Partial<VolumeProfileSettings>) => void;
  saveSettings: () => Promise<void>;
  loadSettings: () => Promise<void>;

  // Data actions
  addFootprint: (symbol: string, footprint: FootprintData) => void;
  addDeltaPoint: (symbol: string, delta: DeltaData) => void;
  setProfileSnapshot: (symbol: string, snapshot: VolumeProfileSnapshot) => void;
  clearSymbolData: (symbol: string) => void;

  // Fetch actions
  fetchProfile: (symbol: string) => Promise<void>;
  fetchFootprints: (symbol: string, limit?: number) => Promise<void>;
  fetchDeltaHistory: (symbol: string) => Promise<void>;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useVolumeProfileStore = create<VolumeProfileState>((set, get) => ({
  // Initial state
  settings: { ...DEFAULT_VOLUME_PROFILE_SETTINGS },
  footprintData: {},
  deltaHistory: {},
  profileSnapshots: {},
  isSyncing: false,
  lastSyncAt: null,
  lastSyncError: null,

  // Settings actions
  setSettings: (settings) => set({ settings }),

  updateSettings: (partial) =>
    set((state) => ({
      settings: { ...state.settings, ...partial, updatedAt: Date.now() },
    })),

  saveSettings: async () => {
    set({ isSyncing: true, lastSyncError: null });
    try {
      const { settings } = get();
      const response = await fetch(
        `${CONFIG.getBackendUrl()}/api/volume-profile-settings`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(settings),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to save settings: ${response.status}`);
      }
      set({ isSyncing: false, lastSyncAt: Date.now() });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to save volume profile settings:', errorMessage);
      set({ isSyncing: false, lastSyncError: errorMessage });
    }
  },

  loadSettings: async () => {
    try {
      const response = await fetch(
        `${CONFIG.getBackendUrl()}/api/volume-profile-settings`
      );
      if (response.ok) {
        const settings = await response.json();
        set({ settings, lastSyncAt: Date.now() });
      }
    } catch (error) {
      console.error('Failed to load volume profile settings:', error);
    }
  },

  // Data actions
  addFootprint: (symbol, footprint) =>
    set((state) => {
      const existing = state.footprintData[symbol] || [];
      // Keep last MAX_FOOTPRINTS_PER_SYMBOL entries
      const updated = [...existing, footprint].slice(-MAX_FOOTPRINTS_PER_SYMBOL);
      return { footprintData: { ...state.footprintData, [symbol]: updated } };
    }),

  addDeltaPoint: (symbol, delta) =>
    set((state) => {
      const existing = state.deltaHistory[symbol] || [];
      // Keep last MAX_DELTA_POINTS_PER_SYMBOL entries
      const updated = [...existing, delta].slice(-MAX_DELTA_POINTS_PER_SYMBOL);
      return { deltaHistory: { ...state.deltaHistory, [symbol]: updated } };
    }),

  setProfileSnapshot: (symbol, snapshot) =>
    set((state) => ({
      profileSnapshots: { ...state.profileSnapshots, [symbol]: snapshot },
    })),

  clearSymbolData: (symbol) =>
    set((state) => {
      const { [symbol]: _fp, ...footprintData } = state.footprintData;
      const { [symbol]: _dh, ...deltaHistory } = state.deltaHistory;
      const { [symbol]: _ps, ...profileSnapshots } = state.profileSnapshots;
      return { footprintData, deltaHistory, profileSnapshots };
    }),

  // Fetch actions
  fetchProfile: async (symbol) => {
    try {
      const response = await fetch(
        `${CONFIG.getBackendUrl()}/api/volume-profile/${encodeURIComponent(symbol)}`
      );
      if (response.ok) {
        const snapshot = await response.json();
        get().setProfileSnapshot(symbol, snapshot);
      }
    } catch (error) {
      console.error(`Failed to fetch volume profile for ${symbol}:`, error);
    }
  },

  fetchFootprints: async (symbol, limit = 100) => {
    try {
      const response = await fetch(
        `${CONFIG.getBackendUrl()}/api/footprint/${encodeURIComponent(symbol)}?limit=${limit}`
      );
      if (response.ok) {
        const footprints = await response.json();
        set((state) => ({
          footprintData: { ...state.footprintData, [symbol]: footprints },
        }));
      }
    } catch (error) {
      console.error(`Failed to fetch footprints for ${symbol}:`, error);
    }
  },

  fetchDeltaHistory: async (symbol) => {
    try {
      const response = await fetch(
        `${CONFIG.getBackendUrl()}/api/cumulative-delta/${encodeURIComponent(symbol)}`
      );
      if (response.ok) {
        const history = await response.json();
        set((state) => ({
          deltaHistory: { ...state.deltaHistory, [symbol]: history },
        }));
      }
    } catch (error) {
      console.error(`Failed to fetch delta history for ${symbol}:`, error);
    }
  },
}));

// ============================================================================
// Convenience Selectors
// ============================================================================

export const selectVolumeProfileEnabled = (state: VolumeProfileState) =>
  state.settings.enabled;

export const selectShowCumulativeDelta = (state: VolumeProfileState) =>
  state.settings.showCumulativeDelta;

export const selectShowPoc = (state: VolumeProfileState) =>
  state.settings.showPoc;

export const selectShowValueArea = (state: VolumeProfileState) =>
  state.settings.showValueArea;
